import { makeAutoObservable, runInAction } from 'mobx';
import type { FHRequest } from '../types';
import { requestApi } from '../api/request_api';

/**
 * Assistant work lists: available (unclaimed), claimed (my work) and
 * closed (completed work). Any approved assistant can work; there is no
 * shift state.
 */
export class WorkStore {
  // Available requests (unclaimed)
  availableRequests: FHRequest[] = [];
  availableLoading = false;
  availableCursor: string | null = null;
  availableHasMore = true;

  // Claimed requests (my work)
  claimedRequests: FHRequest[] = [];
  claimedLoading = false;
  claimedCursor: string | null = null;
  claimedHasMore = true;

  // Closed requests (completed work)
  closedRequests: FHRequest[] = [];
  closedLoading = false;
  closedCursor: string | null = null;
  closedHasMore = true;

  // Error state
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  clearError(): void {
    this.error = null;
  }

  /**
   * Fetch available requests
   */
  async fetchAvailableRequests(refresh = false): Promise<void> {
    if (this.availableLoading) return;
    if (!refresh && !this.availableHasMore) return;

    runInAction(() => {
      this.availableLoading = true;
      this.error = null;
      if (refresh) {
        this.availableCursor = null;
        this.availableHasMore = true;
      }
    });

    try {
      const cursor = refresh ? undefined : this.availableCursor || undefined;
      const response = await requestApi.listAvailable(cursor);

      runInAction(() => {
        this.availableRequests = refresh
          ? response.items
          : [...this.availableRequests, ...response.items];
        this.availableCursor = response.cursor;
        this.availableHasMore = response.has_more;
        this.availableLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.availableLoading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch available requests';
      });
    }
  }

  /**
   * Hide a task from the Available list ("not for me"). Removes it locally
   * right away; returns false (and sets error) if the server refused.
   */
  async declineRequest(requestKey: string, reason: number): Promise<boolean> {
    const previous = this.availableRequests;
    runInAction(() => {
      this.availableRequests = previous.filter((r) => r.key !== requestKey);
    });
    try {
      await requestApi.decline(requestKey, reason);
      return true;
    } catch (err) {
      runInAction(() => {
        this.availableRequests = previous;
        this.error = err instanceof Error ? err.message : 'Failed to hide request';
      });
      return false;
    }
  }

  /**
   * Undo a hide and reload the Available list so the task is back in place.
   */
  async undoDeclineRequest(requestKey: string): Promise<void> {
    try {
      await requestApi.undoDecline(requestKey);
      await this.fetchAvailableRequests(true);
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to unhide request';
      });
    }
  }

  /**
   * Fetch claimed requests
   */
  async fetchClaimedRequests(refresh = false): Promise<void> {
    if (this.claimedLoading) return;
    if (!refresh && !this.claimedHasMore) return;

    runInAction(() => {
      this.claimedLoading = true;
      this.error = null;
      if (refresh) {
        this.claimedCursor = null;
        this.claimedHasMore = true;
      }
    });

    try {
      const cursor = refresh ? undefined : this.claimedCursor || undefined;
      const response = await requestApi.listClaimed(cursor);

      runInAction(() => {
        this.claimedRequests = refresh
          ? response.items
          : [...this.claimedRequests, ...response.items];
        this.claimedCursor = response.cursor;
        this.claimedHasMore = response.has_more;
        this.claimedLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.claimedLoading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch claimed requests';
      });
    }
  }

  /**
   * Fetch closed requests
   */
  async fetchClosedRequests(refresh = false): Promise<void> {
    if (this.closedLoading) return;
    if (!refresh && !this.closedHasMore) return;

    runInAction(() => {
      this.closedLoading = true;
      this.error = null;
      if (refresh) {
        this.closedCursor = null;
        this.closedHasMore = true;
      }
    });

    try {
      const cursor = refresh ? undefined : this.closedCursor || undefined;
      const response = await requestApi.listClosed(cursor);

      runInAction(() => {
        this.closedRequests = refresh
          ? response.items
          : [...this.closedRequests, ...response.items];
        this.closedCursor = response.cursor;
        this.closedHasMore = response.has_more;
        this.closedLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.closedLoading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch closed requests';
      });
    }
  }

  /**
   * Claim a request
   */
  async claimRequest(requestKey: string): Promise<FHRequest | undefined> {
    this.error = null;
    try {
      const request = await requestApi.claim(requestKey);

      runInAction(() => {
        this.availableRequests = this.availableRequests.filter(
          (r) => r.key !== requestKey
        );
        this.claimedRequests = [request, ...this.claimedRequests];
      });

      return request;
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to claim request';
      });
      return undefined;
    }
  }

  /**
   * Release a claimed request back to the available queue
   */
  async releaseRequest(requestKey: string): Promise<FHRequest> {
    const request = await requestApi.release(requestKey);

    runInAction(() => {
      this.claimedRequests = this.claimedRequests.filter(
        (r) => r.key !== requestKey
      );
      if (!this.availableRequests.some((r) => r.key === requestKey)) {
        this.availableRequests = [request, ...this.availableRequests];
      }
    });

    return request;
  }

  /**
   * Reset store state
   */
  reset(): void {
    this.availableRequests = [];
    this.claimedRequests = [];
    this.closedRequests = [];
    this.availableCursor = null;
    this.availableHasMore = true;
    this.claimedCursor = null;
    this.claimedHasMore = true;
    this.closedCursor = null;
    this.closedHasMore = true;
    this.error = null;
  }
}

export const workStore = new WorkStore();
